import { useEffect, useState, useCallback } from "react";
import * as sentry from "@sentry/react";
import { useParams, useNavigate } from "react-router-dom";
import { TextButton, InputTextarea } from "@artprov/design-system";
import HeadingGroup from "../../common/components/headingGroup/HeadingGroup";
import InfoFactura from "./components/InfoInvoice";
import InfoProveedor from "./components/InfoProveedores";
import { getDetalleFactura, descargarFactura } from "../../services/compra.services";
import ApprovalActions from "../../common/components/actions/ApprovalActions.jsx";
import ModalApproval from "./components/ModalApproval.jsx";
import { useFacturaStore } from "../../common/zustand/factura.store.js";
import Alert from "../../common/components/alert/Alert.jsx";
import { parseOracleErrorMessage } from "../../common/utils/functions/parseOracleErrorMessage.js";
import InvoiceHistory from "./components/InvoiceHistory.jsx";
import { useComprasAuth } from "../../common/hooks/useComprasAuth.js";
import FileDownloadList from "./components/FileDownloadList.jsx";
import Spinner from "../../common/components/Spinner";

export default function DetalleFactura() {
  const { idComprobante } = useParams();
  const { username, userDetails } = useComprasAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({})
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [approvalMode, setApprovalMode] = useState("aprobado");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successAlertMessage, setSuccessAlertMessage] = useState("");
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorAlertMessage, setErrorAlertMessage] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [loading, setLoading] = useState(true);

  const setFacturaActual = useFacturaStore((state) => state.setFacturaActual);
  const setDetalleFacturaProveedor = useFacturaStore((state) => state.setDetalleFacturaProveedor);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  const fetchApi = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDetalleFactura(idComprobante, userDetails?.CENTRO_COSTO || 0, username)
      setData(response)
      if (response && typeof response === 'object') {
        setFacturaActual(response)
        setDetalleFacturaProveedor(response)
        setShowErrorAlert(false)
        setErrorAlertMessage("")
      }
    } catch (error) {
      sentry.captureException(error);
      setErrorAlertMessage(parseOracleErrorMessage(error));
      setShowErrorAlert(true);
    } finally {
      setLoading(false);
    }
  }, [idComprobante, userDetails, username, setFacturaActual, setDetalleFacturaProveedor])

  useEffect(() => {
    fetchApi()
  }, [fetchApi])

  useEffect(() => { }, [idComprobante])

  const handleActionSelect = (action) => {
    const descripcion = String(action?.DESCRIPCION || "").toLowerCase();
    if (descripcion.includes("aprobar")) {
      setApprovalMode("aprobado");
      setIsApprovalModalOpen(true);
    } else if (descripcion.includes("rechaz")) {
      setApprovalMode("rechazado");
      setIsApprovalModalOpen(true);
    } else if (descripcion.includes("progreso")) {
      setApprovalMode("en progreso");
      setIsApprovalModalOpen(true);
    }
  };
  const handleDownloadClick = async () => {
    if (!idComprobante) return;
    setIsDownloading(true);
    try {
      const { blob, filename } = await descargarFactura(idComprobante);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `factura_${idComprobante}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccessAlertMessage("La factura se descargó correctamente");
      setShowSuccessAlert(true);
    } catch (error) {
      sentry.captureException(error);
    } finally {
      setIsDownloading(false);
    }
  };
  const isDisabled = isDownloading || !idComprobante;
  return (
    <div>
      {loading && <Spinner label="Cargando detalle de la factura..." />}
      {showSuccessAlert && (
        <Alert
          variant="success"
          description={successAlertMessage}
          onClose={() => setShowSuccessAlert(false)}
          autoHideMs={5000}
        />
      )}
      {showErrorAlert && (
        <Alert
          variant="error"
          description={errorAlertMessage}
          onClose={() => setShowErrorAlert(false)}
        />
      )}
      <div className="mt-4 mb-6 flex flex-col items-start gap-2">
        <TextButton
          IconLeft="keyboard_arrow_left"
          size="extra-small"
          descriptionTextVariant="button3"
          onClick={() => navigate("/")}
        >
          Volver
        </TextButton>

        <HeadingGroup
          title="Detalle de factura"
          className="m-0"
        />
      </div>
      <div className="bg-[#F5F5F5] p-5 flex flex-col gap-4">
        <InfoFactura data={data} idComprobante={idComprobante} />
        <InfoProveedor data={data} />
      </div>
      <div className="mt-5 mb-5">
        <InvoiceHistory idComprobante={idComprobante} refreshKey={historyRefreshKey} />
      </div>
      <div className="mt-5 mb-5">
        <InputTextarea
          disabled={true}
          value={data?.COMENTARIOS || ""}
          label="Comentario"
          placeholder=""
        />
      </div>
      <div className="w-full flex justify-end items-center gap-5 mt-5 mb-5">
        {/* <TextButton
          onClick={handleDownloadClick}
          disabled={true}
          IconLeft="download"
        >
          Descargar factura
        </TextButton>
        */ }
        <ApprovalActions data={data} onActionSelect={handleActionSelect} />
        <ModalApproval
          isOpen={isApprovalModalOpen}
          onClose={() => setIsApprovalModalOpen(false)}
          idComprobante={idComprobante}
          amount={data?.IMPORTE}
          mode={approvalMode}
          onApprove={({ mode, comment }) => {
            // Mapeo dinámico de estados y mensajes
            const statusMap = {
              'rechazado': { estado: 'Rechazado', mensaje: 'Se rechazó la factura exitosamente' },
              'aprobado': { estado: 'Aprobado', mensaje: 'Se aprobó la factura exitosamente' },
              'en progreso': { estado: 'En progreso', mensaje: 'La factura se marcó como "En progreso" exitosamente.' }
            };

            const normalizedMode = String(mode || '').toLowerCase();
            const status = statusMap[normalizedMode] || statusMap['aprobado'];

            setData((prev) => ({ ...(prev || {}), ESTADOCOMPROBANTE: status.estado, COMENTARIOS: comment }));
            setSuccessAlertMessage(status.mensaje);
            setShowSuccessAlert(true);
            setHistoryRefreshKey((prev) => prev + 1);
          }}
          onError={(error) => {
            setErrorAlertMessage(parseOracleErrorMessage(error));
            setShowErrorAlert(true);
          }}
        />
      </div>
      <FileDownloadList idComprobante={idComprobante} />
    </div>
  );
}
